import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// Nous n'utilisons plus useAuth car nous utilisons directement supabase
import { toast } from 'react-hot-toast';
import { supabase } from '../../supabaseClient';
import logoImg from '../../assets/img/logo-cgciparis.png';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  
  // Nous n'utilisons plus le contexte d'authentification car nous utilisons directement supabase
  const navigate = useNavigate();

  // Vérifier si l'utilisateur arrive depuis un lien de réinitialisation
  useEffect(() => {
    // Vérifier si l'URL contient un token de réinitialisation
    const checkResetToken = async () => {
      console.log('ResetPassword: Vérification du token de réinitialisation');
      const hash = window.location.hash;
      console.log('ResetPassword: Hash de l\'URL:', hash);
      
      if (hash && hash.includes('type=recovery')) {
        console.log('ResetPassword: Token de réinitialisation détecté');
        // Supabase gère automatiquement le token dans l'URL
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('ResetPassword: Erreur lors de la récupération de la session:', error);
          toast.error('Lien de réinitialisation invalide ou expiré');
          navigate('/admin/login');
        } else if (!data.session) {
          console.error('ResetPassword: Pas de session trouvée');
          toast.error('Lien de réinitialisation invalide ou expiré');
          navigate('/admin/login');
        } else {
          console.log('ResetPassword: Session valide trouvée');
        }
      } else {
        console.log('ResetPassword: Pas de token de réinitialisation, redirection vers la page de connexion');
        // Rediriger si l'utilisateur accède directement à cette page sans token
        navigate('/admin/login');
      }
    };
    
    checkResetToken();
  }, [navigate]);

  const validatePassword = (password) => {
    // Au moins 8 caractères, une majuscule, une minuscule et un chiffre
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    return regex.test(password);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    
    // Vérifier que les mots de passe correspondent
    if (password !== confirmPassword) {
      setPasswordError('Les mots de passe ne correspondent pas');
      return;
    }
    
    // Vérifier la complexité du mot de passe
    if (!validatePassword(password)) {
      setPasswordError(
        'Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre'
      );
      return;
    }
    
    setIsLoading(true);
    console.log('ResetPassword: Tentative de mise à jour du mot de passe');
    
    try {
      // Utiliser directement Supabase pour mettre à jour le mot de passe
      const { error } = await supabase.auth.updateUser({
        password: password
      });
      
      if (error) {
        console.error('ResetPassword: Erreur lors de la mise à jour du mot de passe:', error);
        throw error;
      }
      
      console.log('ResetPassword: Mot de passe mis à jour avec succès');
      setPasswordChanged(true);
      toast.success('Mot de passe mis à jour avec succès');
    } catch (error) {
      console.error('ResetPassword: Erreur de mise à jour du mot de passe:', error);
      toast.error(error.message || 'Une erreur est survenue lors de la mise à jour du mot de passe');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        <div className="flex flex-col items-center">
          <img src={logoImg} alt="CGCI Paris Logo" className="h-24 mb-4" />
          <h2 className="mt-3 text-center text-3xl font-extrabold text-gray-900">
            Réinitialisation du mot de passe
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {passwordChanged 
              ? 'Votre mot de passe a été mis à jour avec succès' 
              : 'Veuillez définir votre nouveau mot de passe'}
          </p>
        </div>
        
        {passwordChanged ? (
          <div className="mt-8 space-y-6">
            <div className="bg-green-50 p-4 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Mot de passe mis à jour</h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
                  </div>
                  <div className="mt-4">
                    <button
                      type="button"
                      className="inline-flex items-center px-5 py-2 border border-transparent text-sm font-medium rounded-md shadow-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-200"
                      onClick={handleBackToLogin}
                    >
                      Retour à la connexion
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleResetPassword}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="password" className="sr-only">Nouveau mot de passe</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-orange-500 focus:border-orange-500 focus:z-10 sm:text-sm"
                  placeholder="Nouveau mot de passe"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="confirm-password" className="sr-only">Confirmer le mot de passe</label>
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-orange-500 focus:border-orange-500 focus:z-10 sm:text-sm"
                  placeholder="Confirmer le mot de passe"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            </div>

            {passwordError && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Erreur de mot de passe
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{passwordError}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="text-xs text-gray-600 p-3 bg-gray-50 rounded-md mt-2">
              <p className="font-medium">Le mot de passe doit contenir au moins :</p>
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>8 caractères</li>
                <li>Une lettre majuscule</li>
                <li>Une lettre minuscule</li>
                <li>Un chiffre</li>
              </ul>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                  isLoading ? 'bg-orange-400' : 'bg-orange-600 hover:bg-orange-700'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-200 shadow-md`}
              >
                {isLoading ? (
                  <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                    <svg className="animate-spin h-5 w-5 text-orange-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </span>
                ) : null}
                Réinitialiser le mot de passe
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
